
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
import LinkTokenCreate from '../../../components/docs/LinkTokenCreate';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: "Verify users' account ownership and reduce fraud"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Identity'}>{metadata.description}</Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Identity'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/identity/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Plaid's Identity product helps you verify users' identities by accessing information on file with their financial institution. Using Identity, you can access a user's phone number, email address, mailing address, and name. This can be used to fight fraud by verifying that the linked financial institution account is owned by the same user who created an account with you.`}</p>
    <YouTubeEmbed videoId='ILSSdpgGBaU' alignment='full' title='Plaid Identity in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Identity works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <p>{`Plaid provides two endpoints for Identity: `}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`/identity/get`}</inlineCode>{` retrieves the user's name and contact information from their financial institution`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/identity/match`}</inlineCode>{` allows you to provide a name and contact information for a user and returns a set of match scores indicating how well that information matches the information on file with their financial institution, simplifying the process of using identity data for account ownership verification. `}</li>
    </ul>
    <p>{`Both endpoints can be used to reduce fraud, improve user onboarding and conversion, and to complement Know Your Customer (KYC) compliance checks.`}</p>
    <p>{`Identity can be used to verify all major account types supported by Plaid, including checking and savings accounts, credit cards, brokerage accounts, and loans. Identity coverage can be used to verify an account before initiating an ACH transaction: 97% of Items initialized with `}<a parentName="p" {...{
        "href": "/docs/auth"
      }}>{`Auth`}</a>{` provide Identity data as well.`}</p>
    <p>{`Depending on your use case, you may want to verify the identity of all users, or only some. For example, you might want to verify the identity of any user initiating a funds transfer, or you might only verify users who you have identified as being higher risk, based on data such as email address, location, financial institution, or activity patterns.`}</p>
    <p>{`Plaid Identity can be combined with `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Plaid Identity Verification`}</a>{` in a single workflow to provide full identity verification and fraud reduction. `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{` is used for KYC, to verify that the user of your app is the person they claim to be, while Identity is used to confirm that the ownership information on their linked bank or credit card account matches this verified identity.`}</p>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <ol>
      <li parentName="ol">{`Create a Link token by calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`.`}
        <ul parentName="li">
          <li parentName="ul">{`If you are using Identity as a best-effort adjunct to other products, like Auth, put `}<inlineCode parentName="li">{`identity`}</inlineCode>{` in the `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{` array.`}</li>
          <li parentName="ul">{`If you want to require Identity and fail sessions where Identity data is not available, put `}<inlineCode parentName="li">{`identity`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Initialize Link with the Link token from the previous step. For more details, see `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link`}</a>{`.`}</li>
      <li parentName="ol">{`Once the user has successfully completed the Link flow, exchange the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Once you have the `}<inlineCode parentName="li">{`access_token`}</inlineCode>{`, call `}<inlineCode parentName="li">{`/identity/get`}</inlineCode>{` to get data, or `}<inlineCode parentName="li">{`/identity/match`}</inlineCode>{` to match data. For more information on `}<inlineCode parentName="li">{`/identity/match`}</inlineCode>{`, including optional streamlined integration modes, see `}<a parentName="li" {...{
          "href": "#identity-match"
        }}>{`Identity Match`}</a>{`.`}</li>
    </ol>
    <h4 {...{
      "id": "getting-identity-data"
    }}>{`Getting Identity data`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{` endpoint provides you with several pieces of information. The name is guaranteed to be available; the email address, phone number, and address are usually available, but may be `}<inlineCode parentName="p">{`null`}</inlineCode>{` otherwise.`}</p>
    <h5 {...{
      "id": "sample-identity-data"
    }}>{`Sample Identity data`}</h5>
    <p>{`Identity data returned by `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{` includes owners' names, addresses, email addresses, and phone numbers as reported by the financial institution. In the case of an Item containing accounts with multiple owners, all owners' information will be provided. For business accounts, the account owner's name can be that of a business.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Identity data",
        "#Sample": true,
        "Identity": true,
        "data": true
      }}>{`"owners": [
  {
    "addresses": [
      {
        "data": {
          "city": "Malakoff",
          "country": "US",
          "postal_code": "14236",
          "region": "NY",
          "street": "2992 Cameron Road"
        },
        "primary": true
      }
    ],
    "emails": [
      {
        "data": "accountholder0@example.com",
        "primary": true,
        "type": "primary"
      },
      {
        "data": "accountholdersecondary0@example.com",
        "primary": false,
        "type": "secondary"
      }
    ],
    "names": [
      "Alberta Bobbeth Charleson"
    ],
    "phone_numbers": [
      {
        "data": "1112223333",
        "primary": true,
        "type": "home"
      }
    ]
  }
]
`}</code></pre>
    <h5 {...{
      "id": "typical-identity-fill-rates-by-field"
    }}>{`Typical identity fill rates by field`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Typical fill rate`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`100%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Email address`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`98%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Address`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`93%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Phone number`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`90%`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "identity-match"
    }}>{`Identity Match`}</h4>
    <p>{`Using `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{`, Identity data fields such as names, addresses, email addresses, and phone numbers can be matched against the owners' identity information on connected accounts. Multiple fields are matched in a single request, and a separate score is returned for each field to indicate how closely it matched. Customers using Identity Match can experience onboarding conversion improvements of 20% or more, without increasing fraud rates, when using Identity Match versus using their own matching algorithms.`}</p>
    <Callout mdxType="Callout">
      <p>{`If you are using `}<a parentName="p" {...{
          "href": "/docs/auth"
        }}>{`Auth`}</a>{`, you can optionally integrate Identity Match via the Dashboard with no additional code. For details, see `}<a parentName="p" {...{
          "href": "#integrating-identity-match-via-the-account-verification-dashboard"
        }}>{`Integrating Identity Match via the Account Verification Dashboard`}</a>{`.`}</p>
    </Callout>
    <Image src='/assets/img/docs/identity-match.png' caption="Example Identity Match results in the Account Verification Dashboard" width='624' expandable={true} mdxType="Image" />
    <p>{`If you are already using `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{`, you can further enhance your verification process by enabling Financial Account Matching in your Identity Verification template.
This feature allows you to match the data collected during KYC without having to specify the `}<inlineCode parentName="p">{`user`}</inlineCode>{` field in your `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` request.
To ensure accurate matching, it's important to maintain consistency in the `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` used for end users across all products.`}</p>
    <p>{`For the data returned by `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` and how to interpret each field's match score — including the name, phone number, email, and address score tables and sample response — see `}<a parentName="p" {...{
        "href": "/docs/identity/interpreting-identity-match"
      }}>{`Interpreting Identity Match Scores`}</a>{`.`}</p>
    <h5 {...{
      "id": "using-identity-match-with-micro-deposit-or-database-items"
    }}>{`Using Identity Match with micro-deposit or database Items`}</h5>
    <p>{`Items verified via `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day"
      }}>{`Same-Day Micro-deposits`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/instant#instant-micro-deposits"
      }}>{`Instant micro-deposits`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/database-auth"
      }}>{`Database Auth`}</a>{` are not typically compatible with any other Plaid products besides Auth and Transfer. An exception is Identity Match, which can be used with these Items if they were previously seen on the Plaid network (approximately 30% of these Items).`}</p>
    <p>{`Note that this applies to the Identity Match endpoint only; Items created using these flows are not compatible with `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{`.`}</p>
    <p>{`To use Identity Match with micro-deposit or database Items, you have two options:`}</p>
    <ul>
      <li parentName="ul">{`For a low-code option that allows you to run the Identity Match check within the Link flow, use the `}<a parentName="li" {...{
          "href": "#integrating-identity-match-via-the-account-verification-dashboard"
        }}>{`Account Verification Dashboard`}</a>{`.`}</li>
      <li parentName="ul">{`To run the Identity Match check outside the Link flow, continue with the `}<a parentName="li" {...{
          "href": "#integration-instructions"
        }}>{`integration instructions in this section`}</a>{`.`}</li>
    </ul>
    <h6 {...{
      "id": "integration-instructions"
    }}>{`Integration instructions`}</h6>
    <ol>
      <li parentName="ol">{`To enable the Item for Identity Match, `}<inlineCode parentName="li">{`identity`}</inlineCode>{` must be included in the `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{`, `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{`, or `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` array during the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call at Item creation. (For this flow, `}<inlineCode parentName="li">{`identity`}</inlineCode>{` should not be included in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array, since including any other product than `}<inlineCode parentName="li">{`auth`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array can prevent micro-deposit or database verification flows from activating.)`}
        <ul parentName="li">
          <li parentName="ul">{`Items created via micro-deposits or Database Auth cannot have products or consent added through `}<a parentName="li" {...{
              "href": "/docs/link/update-mode/"
            }}>{`update mode`}</a>{`, so Identity consent must be requested at Item creation.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/identity/match`}</inlineCode>{`.`}</li>
    </ol>
    <p>{`If the Item is supported by Identity Match, you will receive results. If the Item was not previously seen on the Plaid network and therefore cannot be used with Identity Match, you will receive a `}<inlineCode parentName="p">{`PRODUCTS_NOT_SUPPORTED`}</inlineCode>{` error and will not be billed for the API call.`}</p>
    <h4 {...{
      "id": "integrating-identity-match-via-the-account-verification-dashboard"
    }}>{`Integrating Identity Match via the Account Verification Dashboard`}</h4>
    <p>{`If you are using `}<a parentName="p" {...{
        "href": "/docs/auth"
      }}>{`Auth`}</a>{`, you can integrate Identity Match via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Account Verification Dashboard`}</a>{`. This is a lower-code option that allows you to rely on Plaid Link for checking match scores and handling failed sessions. `}</p>
    <p>{`When enabling Identity in this way, the Identity check will be incorporated into the Link flow.`}</p>
    <p>{`This configuration option can only be used if Auth and Identity are the only required Plaid products you are using in the session. If you need access to other products, use the `}<a parentName="p" {...{
        "href": "#integration-overview"
      }}>{`standard integration flow`}</a>{`.`}</p>
    <ol>
      <li parentName="ol">{`In the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/account-verification"
        }}>{`Account Verification Dashboard`}</a>{`, go to the Overview pane, find the Link customization you want to use, and click "Add IDM rules" next to it. (This button appears only if you have Production access to Identity Match.)`}</li>
      <li parentName="ol">{`Select the rules to use for Identity Match. Choosing "Recommended rules" applies Plaid's default ruleset as a starting point, which you can then adjust. Typically, the match threshold for each field should be set to a score of 70 or higher.`}</li>
      <li parentName="ol">{`When calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`:`}
        <ul parentName="li">
          <li parentName="ul">{`Ensure that `}<inlineCode parentName="li">{`identity`}</inlineCode>{` is in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array (or `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{` array, if you want to allow customers to link accounts if their bank does not support Identity).`}</li>
          <li parentName="ul">{`Set the `}<inlineCode parentName="li">{`link_customization_name`}</inlineCode>{` to the customization you selected in Step 1 (this is optional if you are using the `}<inlineCode parentName="li">{`default`}</inlineCode>{` customization). `}</li>
          <li parentName="ul">{`Provide the identity data you have about the user, such as their `}<inlineCode parentName="li">{`legal_name`}</inlineCode>{`, `}<inlineCode parentName="li">{`phone_number`}</inlineCode>{`, `}<inlineCode parentName="li">{`address`}</inlineCode>{`, and `}<inlineCode parentName="li">{`email_address`}</inlineCode>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Launch Link using the Link token. When the user goes through the Link flow, the identity data you supplied in the previous step will be compared to the data retrieved from their linked financial account. If the result based on your configuration is a pass, they will see a success screen. If it is a failure, they will see an error.`}</li>
      <li parentName="ol">{`Watch for the `}<inlineCode parentName="li">{`IDENTITY_MATCH_PASSED`}</inlineCode>{` or `}<inlineCode parentName="li">{`IDENTITY_MATCH_FAILED`}</inlineCode>{` event from Link to detect the result of Identity Match in the Link flow. `}</li>
      <li parentName="ol">{`(Optional) To view the pass/fail status of a previous Identity Match session, call `}<inlineCode parentName="li">{`/identity/match/list`}</inlineCode>{` or check the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/account-verification"
        }}>{`Dashboard`}</a>{`.`}</li>
    </ol>
    <p>{`If the Identity Match check fails, you will not receive a `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` event or `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, but if you want to proceed with creating an Item anyway, you can obtain the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` by calling `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`If Identity is enabled via the Account Verification Dashboard, you will be billed for Identity Match as long as Plaid could obtain any Identity data from the Item, even if you did not exchange the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`. If Plaid was unable to obtain any Identity data for the Item, you will not be billed.`}</p>
    </Callout>
    <LinkTokenCreate useCases={[{
      useCase: 'Integrating Identity Match via the Account Verification Dashboard',
      comment: 'Sample call when integrating Identity Match via the Account Verification Dashboard',
      config: {
        user: {
          client_user_id: 'user-id',
          phone_number: '+1-111-222-3333',
          email_address: 'accountholder0@example.com',
          legal_name: 'Alberta Bobbeth Charleson',
          address: {
            street: '2992 Cameron Rd.',
            city: 'Malakoff',
            region: 'NY',
            postal_code: '14236',
            country: 'US'
          }
        },
        client_name: 'Personal Finance App',
        products: ['auth'],
        required_if_supported_products: ['identity'],
        country_codes: ['US'],
        language: 'en',
        webhook: 'https://sample-web-hook.com',
        redirect_uri: 'https://domainname.com/oauth-page.html'
      }
    }]} mdxType="LinkTokenCreate" />
    <h4 {...{
      "id": "testing-identity"
    }}>{`Testing Identity`}</h4>
    <p>{`Identity can be tested in Sandbox against test data.`}</p>
    <p>{`To test configuration options beyond those offered by the default Sandbox user, create a `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`custom Sandbox user`}</a>{` with the identity data you want.`}</p>
    <p>{`For testing against a user with accounts at several institutions, `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profiles`}</a>{` carry a consistent identity -- name, address, email, date of birth, and SSN -- across every Item connected to the profile.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Do not send real user data or PII to the Sandbox environment, including for use with `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{`. Use custom Sandbox users or Sandbox Profiles to construct the identity data you need to test against.`}</p>
    </Callout>
    <p>{`For details on testing Identity Match with Same-Day Micro-deposits, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing/#testing-same-day-micro-deposits"
      }}>{`Testing Same-Day Micro-deposits`}</a>{`.`}</p>
    <p>{`When testing Identity Match using the default `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` custom Sandbox user, you can expect the following identity data to be used for matching against:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Default Sandbox identity data",
        "switcher": "false",
        "#Default": true,
        "Sandbox": true,
        "identity": true,
        "data": true
      }}>{`Alberta Bobbeth Charleson
2992 Cameron Rd.
Malakoff, NY 14236
Phone: +1-111-222-3333
Email: accountholder0@example.com
`}</code></pre>
    <p>{`The default Sandbox account also carries additional identity values: two further phone numbers and email addresses, and a second address (2493 Leisure Lane, San Matias, CA 93405-2255). `}<inlineCode parentName="p">{`/identity/match`}</inlineCode>{` returns the highest score across all values on the account, so test data intended to produce a mismatch should not match any of them.`}</p>
    <h4 {...{
      "id": "identity-document-upload"
    }}>{`Identity Document Upload`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "/docs/identity/identity-document-upload"
      }}>{`Identity Document Upload`}</a>{` add-on can be used to verify account ownership based on a bank statement uploaded by an end user. This feature is intended primarily for use with Items created via loginless Auth flows, such as Same-Day Micro-deposits, Instant Micro-deposits, or Database Auth. For more details, see `}<a parentName="p" {...{
        "href": "/docs/identity/identity-document-upload"
      }}>{`Identity Document Upload`}</a>{`.`}</p>
    <h4 {...{
      "id": "sample-app-and-example-code"
    }}>{`Sample app and example code`}</h4>
    <p>{`For a real-life example of an app that incorporates Identity, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that fetches Identity data in order to verify identity prior to a funds transfer. The Identity code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L133-L176"
      }}>{`items.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "identity-pricing"
    }}>{`Identity pricing`}</h4>
    <p>{`Identity is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#one-time-fee"
      }}>{`one-time fee model`}</a>{`, meaning you will be billed once for each Item with Identity added to it. Identity Match is billed on `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`a per-request flat-fee basis`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "identity-availability-by-country"
    }}>{`Identity availability by country`}</h4>
    <p>{`Identity and Identity Match are available in all supported countries (US, Canada, and Europe, including the UK). In Canada, Identity Match cannot be requested via the Production access form and is available via a Growth or Custom plan only. To request access to Identity Match in Canada, contact your account manager or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`sales`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To get started building with Identity, see `}<a parentName="p" {...{
        "href": "/docs/identity/add-to-app"
      }}>{`Add Identity to your App`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the Launch Center.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle='See next steps to launch in Production' link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "getting-identity-data",
        level: 4,
        title: "Getting Identity data",
      },
{
        id: "identity-match",
        level: 4,
        title: "Identity Match",
      },
{
        id: "integrating-identity-match-via-the-account-verification-dashboard",
        level: 4,
        title: "Integrating Identity Match via the Account Verification Dashboard",
      },
{
        id: "testing-identity",
        level: 4,
        title: "Testing Identity",
      },
{
        id: "identity-document-upload",
        level: 4,
        title: "Identity Document Upload",
      },
{
        id: "sample-app-and-example-code",
        level: 4,
        title: "Sample app and example code",
      },
{
        id: "identity-pricing",
        level: 4,
        title: "Identity pricing",
      },
{
        id: "identity-availability-by-country",
        level: 4,
        title: "Identity availability by country",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
